<script lang="ts">
    export enum ModalSize {
        Full = 'full',
        Small = 'sm',
        Medium = 'md',
        Large = 'lg',
        ExtraLarge = 'xl',
    }

    export enum ModalCloseColor {
        Light = 'light',
        Dark = 'dark',
    }
</script>

<script setup lang="ts">
    import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
    import { __ } from '../stores/expressions';
    import { useElementFocus } from '../composables/useElementFocus';
    import { XIcon } from '@lucide/vue';

    const { restoreFocus, setFocus } = useElementFocus();

    const visible = ref<boolean>(false);

    const closeButton = ref<HTMLElement | null>(null);

    const props = withDefaults(
        defineProps<{
            show?: boolean;
            size?: ModalSize;
            fixedHeight?: boolean;
            padding?: boolean;
            closeColor?: ModalCloseColor;
            autoFocus?: string;
            layer?: 'modal' | 'confirm';
            beforeClose?: () => Promise<boolean> | boolean;
        }>(),
        {
            show: false,
            size: ModalSize.Full,
            fixedHeight: false,
            padding: true,
            closeColor: ModalCloseColor.Dark,
            autoFocus: undefined,
            layer: 'modal',
            beforeClose: undefined,
        }
    );

    const emit = defineEmits<{
        (e: 'close'): void;
    }>();

    const requestClose = async () => {
        if (props.beforeClose) {
            const canClose = await props.beforeClose();
            if (!canClose) return;
        }
        visible.value = false;
    };

    watch(
        () => props.show,
        (newValue, oldValue) => {
            if (newValue) {
                visible.value = true;
            } else if (oldValue) {
                visible.value = false;
            }
        },
        { immediate: true }
    );

    watch(
        () => visible.value,
        (isVisible) => {
            if (isVisible) {
                document.body.classList.add('overflow-hidden');
                visible.value = true;
                nextTick(() => {
                    setFocus(props.autoFocus || closeButton.value, closeButton.value);
                });
            } else {
                document.body.classList.remove('overflow-hidden');
                visible.value = false;
                emit('close');
                restoreFocus();
            }
        }
    );

    const width = computed(() => {
        let width = 'w-full';

        if (props.size === ModalSize.Small) {
            width = 'w-lg';
        } else if (props.size === ModalSize.Medium) {
            width = 'w-2xl';
        } else if (props.size === ModalSize.Large) {
            width = 'w-3xl';
        } else if (props.size === ModalSize.ExtraLarge) {
            width = 'w-4xl';
        }

        return width;
    });

    const closeIconColor = computed(() => {
        return props.closeColor === ModalCloseColor.Light ? 'text-white' : 'text-gray-dark';
    });

    const fixedHeightClasses = computed(() => {
        return props.fixedHeight ? 'h-[calc(100vh-5rem)]' : '';
    });

    const layerClasses = computed(() => {
        return props.layer === 'confirm'
            ? { wrapper: 'z-confirm', backdrop: 'z-confirm-backdrop' }
            : { wrapper: 'z-modal', backdrop: 'z-modal-backdrop' };
    });

    const classes = computed(() => {
        let classes = '';

        if (props.padding) {
            classes += 'p-8';
        } else {
            classes += 'p-0';
        }

        classes += ` ${width.value} ${fixedHeightClasses.value}`;

        return classes;
    });

    const onKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && visible.value) {
            requestClose();
        }
    };

    const onPopstate = () => {
        if (visible.value) {
            requestClose();
        }
    };

    onMounted(() => {
        window.addEventListener('keydown', onKeydown);
        window.addEventListener('popstate', onPopstate);
    });

    onUnmounted(() => {
        document.body.classList.remove('overflow-hidden');
        window.removeEventListener('keydown', onKeydown);
        window.removeEventListener('popstate', onPopstate);
    });
</script>

<template>
    <Teleport to="body">
        <Transition name="slide">
            <div
                v-if="visible"
                data-modal-wrapper
                class="fixed top-0 left-0 h-screen w-screen overflow-auto pointer-events-none"
                :class="layerClasses.wrapper"
            >
                <div class="p-3 sm:p-10 flex items-center min-h-screen justify-center">
                    <div
                        class="bg-white flex-shrink-0 rounded-lg pointer-events-auto max-w-full relative overflow-x-auto custom-scrollbar"
                        :class="classes"
                    >
                        <button
                            ref="closeButton"
                            :aria-label="__('close-modal')"
                            class="absolute flex items-center px-2 py-2 rounded-md text-body-bold cursor-pointer top-1 right-1 z-100 custom-focus"
                            :class="closeIconColor"
                            @click="requestClose()"
                        >
                            <span
                                v-if="[ModalSize.ExtraLarge, ModalSize.Full].includes(size)"
                                class="mr-1 block"
                            >
                                {{ __('close') }}
                            </span>
                            <XIcon class="size-5" />
                        </button>

                        <slot />
                    </div>
                </div>
            </div>
        </Transition>

        <Transition name="fade">
            <div
                v-if="visible"
                class="fixed h-screen w-screen left-0 top-0 bg-gray-dark transition-opacity opacity-75 duration-300"
                :class="[layerClasses.backdrop, { 'pointer-events-none': !visible }]"
                @click="requestClose()"
            />
        </Transition>
    </Teleport>
</template>
